import { Component } from 'react';
import { useLocation } from 'react-router-dom';

// A lazy page chunk that no longer exists on the server: the dashboard was updated while this tab was open.
const isChunkLoadError = (error) => /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|dynamically imported module/i
   .test(`${error?.name || ''} ${error?.message || ''}`);

const buttonClass = 'rounded-lg border px-4 py-2 text-sm font-semibold';

/**
 * Catches a page that fails while rendering, so the user gets an explanation and a way out instead of a
 * blank screen. It resets when the user moves to another page.
 */
class RouteErrorBoundaryInner extends Component {
   constructor(props) {
      super(props);
      this.state = { error: null };
   }

   static getDerivedStateFromError(error) {
      return { error };
   }

   componentDidCatch(error, info) {
      console.error('Page failed to render', error, info?.componentStack);
   }

   componentDidUpdate(previousProps) {
      if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
         this.setState({ error: null });
      }
   }

   render() {
      const { error } = this.state;
      if (!error) {
         return this.props.children;
      }

      const updated = isChunkLoadError(error);
      return (
         <div role="alert" className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
               <h1 className="text-lg font-semibold text-slate-900">
                  {updated ? 'Billable has been updated' : 'This page could not be shown'}
               </h1>
               {updated ? (
                  <p className="mt-2 text-sm text-slate-600">A newer version is available. Reload the page to continue.</p>
               ) : (
                  <>
                     <p className="mt-2 text-sm text-slate-600">
                        Some records on this page are missing information Billable needs to display them. Nothing has been
                        changed, and the rest of Billable keeps working.
                     </p>
                     <p className="mt-2 text-sm text-slate-600">
                        Reload the page to try again. If it happens again, send the details below to your administrator or
                        Billable support so the missing information can be added.
                     </p>
                  </>
               )}
               <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" onClick={() => window.location.reload()} className={`${buttonClass} border-slate-900 bg-slate-900 text-white`}>
                     Reload page
                  </button>
                  <button type="button" onClick={() => window.history.back()} className={`${buttonClass} border-slate-300 text-slate-700`}>
                     Go back
                  </button>
               </div>
               {!updated && (
                  <details className="mt-4 text-xs text-slate-500">
                     <summary className="cursor-pointer">Details for support</summary>
                     <p className="mt-2">Page: {window.location.pathname}</p>
                     <p className="mt-1 break-words">{String(error?.message || error)}</p>
                  </details>
               )}
            </div>
         </div>
      );
   }
}

const RouteErrorBoundary = ({ children }) => {
   const location = useLocation();
   return <RouteErrorBoundaryInner resetKey={location.pathname}>{children}</RouteErrorBoundaryInner>;
};

export default RouteErrorBoundary;
